import React, { useState } from 'react'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { ArrowLeft, Building2, Mail, Phone, Shield } from 'lucide-react'
import { Alert, AlertDescription } from '../ui/alert'
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs'
import { useAuth } from '../../context/AuthContext'
import { useNavigate } from 'react-router-dom'

export function CompanyAuth() {
  const [step, setStep] = useState('identifier')  // Changed from 'email' to 'identifier'
  const [authMethod, setAuthMethod] = useState('email')  // 'email' or 'whatsapp'
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [countryCode, setCountryCode] = useState('+91')
  const [otp, setOtp] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const { login, verifyOTP } = useAuth()
  const navigate = useNavigate()

  const handleIdentifierSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (authMethod === 'email') {
      if (!email || !email.includes('@')) {
        setError('Please enter a valid email address')
        return
      }
    } else {
      if (!phone || phone.length < 10) {
        setError('Please enter a valid phone number')
        return
      }
    }

    setIsLoading(true)
    const result = authMethod === 'email'
      ? await login(email, null, authMethod)
      : await login(null, countryCode + phone, authMethod)

    if (result.success) {
      setStep('otp')
    } else {
      setError(result.message)
    }
    setIsLoading(false)
  }

  const handleOtpSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!otp) {
      setError('Please enter the OTP')
      return
    }

    setIsLoading(true)
    const identifier = authMethod === 'email' ? email : countryCode + phone
    const result = await verifyOTP(identifier, otp, authMethod)

    if (result.success) {
      // Automatic role-based navigation
      if (result.user.role === 'admin') {
        navigate('/admin/dashboard')
      } else if (result.user.role === 'company' || result.user.role === 'non-demo' || result.user.role === 'sponsor') {
        navigate('/company/dashboard')
      }
    } else {
      setError(result.message)
    }
    setIsLoading(false)
  }

  const handleResendOtp = async () => {
    setError('')
    setIsLoading(true)
    const result = authMethod === 'email'
      ? await login(email, null, authMethod)
      : await login(null, countryCode + phone, authMethod)
    if (!result.success) {
      setError(result.message)
    }
    setIsLoading(false)
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-secondary/20 to-primary/5">
      <div className="w-full max-w-md">
        <Card className="border-2">
          <CardHeader className="text-center">
            <div className="mx-auto bg-primary/10 p-3 rounded-full mb-4">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">IFTA Partner Portal Authentication</CardTitle>
            <CardDescription>
              {step === 'identifier'
                ? `Enter your ${authMethod === 'email' ? 'email address' : 'phone number'} to get started`
                : `Enter the OTP sent to your ${authMethod === 'email' ? 'email' : 'WhatsApp'}`}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {step === 'identifier' ? (
              <form onSubmit={handleIdentifierSubmit} className="space-y-4">
                {/* Authentication Method Selector */}
                <Tabs value={authMethod} onValueChange={setAuthMethod} className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="email">Email</TabsTrigger>
                    <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
                  </TabsList>
                </Tabs>

                {/* Input Field based on selected method */}
                {authMethod === 'email' ? (
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        required
                        disabled={isLoading}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Only whitelisted emails can access the platform
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <div className="flex gap-2">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">+</span>
                        <Input
                          value={countryCode.replace('+', '')}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            setCountryCode(value ? '+' + value : '+');
                          }}
                          className="w-[90px] pl-7"
                          placeholder="91"
                          disabled={isLoading}
                          maxLength={3}
                        />
                      </div>
                      <div className="relative flex-1">
                        <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="9876543210"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                          className="pl-10"
                          required
                          disabled={isLoading}
                          maxLength={15}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Enter country code (e.g., 91 for India) and phone number
                    </p>
                  </div>
                )}

                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Sending OTP...' : 'Send OTP'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleOtpSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="otp">Verification Code</Label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="otp"
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      className="pl-10"
                      required
                      disabled={isLoading}
                      maxLength={6}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Check your {authMethod === 'email' ? 'email' : 'WhatsApp'} for the verification code
                  </p>
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? 'Verifying...' : 'Verify OTP'}
                  </Button>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setStep('identifier')
                        setOtp('')
                        setError('')
                      }}
                      disabled={isLoading}
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={handleResendOtp}
                      disabled={isLoading}
                    >
                      Resend OTP
                    </Button>
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-xs text-muted-foreground">
                    Sent to: <span className="font-medium">
                      {authMethod === 'email' ? email : `${countryCode}${phone}`}
                    </span>
                  </p>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <div className="text-center mt-8 text-sm text-muted-foreground">
          <p>Aurika, Mumbai • December 10th, 2025</p>
          <p className="mt-2">
            Need help? Contact support at{' '}
            <a href="mailto:support@indiafintech.com" className="text-primary hover:underline">
              support@indiafintech.com
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}